import React, { useEffect, useMemo, useState } from "react";
import { useHistory, useParams } from "react-router-dom";
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Container,
  Grid,
  Stack,
  Typography,
} from "@mui/material";
import PublicHeader from "../../components/PublicHeader/PublicHeader";
import { REACT_APP_API_BASE_URL } from "../../config";
import { ROUTES } from "../../routes/User/paths";

const buildRoomUrlCandidates = (roomId) => [
  `${REACT_APP_API_BASE_URL}/chat-rooms/rooms/${roomId}?spectator_mode=true&guest_mode=true`,
  `${REACT_APP_API_BASE_URL}/chat-rooms/rooms/${roomId}?spectator_mode=true`,
  `${REACT_APP_API_BASE_URL}/chat-rooms/rooms/${roomId}`,
];

export default function SpectateGuestMode() {
  const history = useHistory();
  const { id } = useParams();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [roomData, setRoomData] = useState(null);

  useEffect(() => {
    let mounted = true;

    const loadRoom = async () => {
      const candidates = buildRoomUrlCandidates(id);

      for (const url of candidates) {
        try {
          const res = await fetch(url);
          if (!res.ok) continue;

          const payload = await res.json();
          if (!mounted) return;

          setRoomData(payload?.data || payload?.data?.room || null);
          setError("");
          setLoading(false);
          return;
        } catch (err) {
          continue;
        }
      }

      if (!mounted) return;
      setError("This room preview is not available for guests right now.");
      setLoading(false);
    };

    loadRoom();

    return () => {
      mounted = false;
    };
  }, [id]);

  const participants = useMemo(() => roomData?.room_participants || [], [roomData]);

  return (
    <Box sx={{ minHeight: "100vh", background: "linear-gradient(180deg, #f7fbff 0%, #ffffff 55%)" }}>
      <PublicHeader scrolled />

      <Container maxWidth="lg" sx={{ pt: 12, pb: 8 }}>
        {loading ? (
          <Stack spacing={2} alignItems="center" sx={{ py: 10 }}>
            <CircularProgress />
            <Typography variant="body1">Loading guest spectate preview...</Typography>
          </Stack>
        ) : error ? (
          <Card sx={{ borderRadius: 3 }}>
            <CardContent>
              <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>
                Guest Preview Unavailable
              </Typography>
              <Typography color="text.secondary" sx={{ mb: 3 }}>
                {error}
              </Typography>
              <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                <Button variant="contained" onClick={() => history.push(`${ROUTES.spectate}/${id}`)}>
                  Log In To Spectate
                </Button>
                <Button variant="outlined" onClick={() => history.push(ROUTES.spectate)}>
                  Browse Rooms
                </Button>
              </Stack>
            </CardContent>
          </Card>
        ) : (
          <Stack spacing={3}>
            <Card sx={{ borderRadius: 3 }}>
              <CardContent>
                <Typography variant="overline" color="primary" sx={{ fontWeight: 700 }}>
                  Guest Mode (View Only)
                </Typography>
                <Typography variant="h4" sx={{ fontWeight: 800, mb: 1 }}>
                  {roomData?.name || "Live Debate Room"}
                </Typography>
                <Typography color="text.secondary" sx={{ mb: 2 }}>
                  {roomData?.description || "Watch live conversations. Create an account to join chat and switch teams."}
                </Typography>
                <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                  <Button variant="contained" onClick={() => history.push(`${ROUTES.spectate}/${id}`)}>
                    Log In For Full Spectate
                  </Button>
                  <Button variant="outlined" onClick={() => history.push(ROUTES.register)}>
                    Create Free Account
                  </Button>
                </Stack>
              </CardContent>
            </Card>

            <Card sx={{ borderRadius: 3 }}>
              <CardContent>
                <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
                  Live Participants
                </Typography>
                {participants.length === 0 ? (
                  <Typography color="text.secondary">
                    No participant list available in guest mode yet.
                  </Typography>
                ) : (
                  <Grid container spacing={2}>
                    {participants.map((participant) => (
                      <Grid item xs={12} sm={6} md={4} key={participant.id}>
                        <Card variant="outlined" sx={{ borderRadius: 2 }}>
                          <CardContent>
                            <Typography sx={{ fontWeight: 700 }}>
                              {participant.username || "Participant"}
                            </Typography>
                            <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                              <Chip
                                size="small"
                                label={Number(participant?.is_admin) > 0 ? "Moderator" : "Participant"}
                              />
                              {participant?.is_online ? <Chip size="small" color="success" label="Online" /> : null}
                            </Stack>
                          </CardContent>
                        </Card>
                      </Grid>
                    ))}
                  </Grid>
                )}
              </CardContent>
            </Card>
          </Stack>
        )}
      </Container>
    </Box>
  );
}
